<template>
  <div
    class="flex flex-column gap-2"
    data-testid="time-off-view-scheduler"
    v-if="timeOff.status === 1 && hasPermission('manage_shifts')"
  >
    <h4 class="m-0" data-testid="time-off-view-scheduler-title">Approve Day Off</h4>
    <p class="m-0">Please check AT LEAST ONE DATE to approve the request!</p>
    <a-table
      data-testid="time-off-view-scheduler-table"
      :row-selection="{
        selectedRowKeys: dates,
        onChange: onSelectChange,
        getCheckboxProps: record => ({
          props: {
            disabled: isDisableDate(record.id)
          }
        })
      }"
      :rowKey="record => record.id"
      size="small"
      :columns="columns"
      :data-source="timeOff.items"
      :scroll="{ y: 200 }"
    >
      <span slot="conflict" slot-scope="text, record">
        <div v-if="getConflictedShift(record.id)" class="flex flex-column gap-1">
          <div class="flex gap-2 align-items-center">
            <b class="w-fit p-0" :style="{ color: 'var(--error-color)' }" type="link"
              >Conflict with Shift №{{ getConflictedShift(record.id).id }}</b
            >
            <a-button
              icon="eye"
              size="small"
              ghost
              title="Open"
              data-testid="time-off-view-scheduler-open-shift-button"
              @click="handleOpenShift(getConflictedShift(record.id).id)"
            />
          </div>

          <span class="flex gap-2"><b>Status:</b> </span>
          <shift-status :shift="getConflictedShift(record.id)" />
          <div>
            <div v-for="row in getCollectedDate(getConflictedShift(record.id))" :key="row.label">
              <b>{{ row.label }}:</b> {{ row.value }}
            </div>
          </div>
        </div>
        <span v-else>-</span>
      </span>

      <template slot="footer">
        <div v-if="dates.length" class="flex justify-content-between" data-testid="time-off-view-scheduler-footer">
          <span data-testid="time-off-view-scheduler-selected-count">{{ `Selected ${dates.length} dates` }}</span>
          <a-button type="danger" @click="handleDeleteDays" data-testid="time-off-view-scheduler-delete-days-button">
            Delete Days
          </a-button>
        </div>
      </template>
    </a-table>
    <signature
      checkbox-label="I've reviewed the time off request and confirm the selected days"
      ref="signatureRef"
      :key="scheduleSignatureKey"
      test-id="time-off-view-scheduler-signature"
      checkbox-wrapper-test-id="time-off-view-scheduler-signature-checkbox-wrapper"
      checkbox-test-id="time-off-view-scheduler-signature-checkbox"
      canvas-container-test-id="time-off-view-scheduler-signature-canvas-container"
      canvas-element-test-id="time-off-view-scheduler-signature-canvas"
      clear-button-test-id="time-off-view-scheduler-signature-clear-button"
      undo-button-test-id="time-off-view-scheduler-signature-undo-button"
      @checked="$emit('update:checked', $event)"
      @save-signature="setSignatureId"
    />
  </div>
</template>

<script>
import Signature from '@/components/signature.vue'
import permissionMixin from '@/mixins/permissionMixin'
import util from '@/util'
import { Button, notification, Table } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import shiftsHelper from '@/helpers/shifts'
import ShiftTableStatus from '@/modules/shifts/components/shift-table-status.vue'

const { mapState, mapActions } = createNamespacedHelpers('timeOff')
const { mapGetters: shiftGetters } = createNamespacedHelpers('shifts')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-table': Table,
    'a-button': Button,
    'shift-status': ShiftTableStatus,
    Signature
  },
  mixins: [permissionMixin, shiftsHelper],
  props: {
    loadingApprove: {
      type: Boolean,
      default: false
    },
    loadingDecline: {
      type: Boolean,
      default: false
    },
    checked: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dates: [],

      columns: [
        {
          title: 'Date',
          key: 'date',
          width: '100px',
          customRender: (_, record) => {
            const { date } = record

            return this.formatDateString(date)
          },
          sorter: (a, b) => {
            const dateA = a.date ? new Date(a.date) : null
            const dateB = b.date ? new Date(b.date) : null

            if (!dateA && !dateB) return 0
            if (!dateA) return 1
            if (!dateB) return -1

            return dateA - dateB
          }
        },
        {
          title: 'Conflict',
          key: 'conflict',
          scopedSlots: {
            customRender: 'conflict'
          },
          sorter: (a, b) => this.getConflictedShift(a.id) - this.getConflictedShift(b.id)
        }
      ],

      signature_id: undefined,

      scheduleSignatureKey: 0,
      scheduleApprovedSignatureKey: 0
    }
  },
  computed: {
    ...shiftGetters(['getShiftStatus']),
    ...mapState({
      timeOff: state => state.timeOff
    }),

    timeZone() {
      return this.$store.state.applicationState.timeZone
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...mapActions(['approveDaysTimeOff']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    handleOpenShift(id) {
      this.openModal({
        type: 'shiftView',
        data: {
          visible: true,
          edit: () => {},
          shift_id: id,
          open_tab: 'info'
        }
      })
    },

    handleDeleteDays() {
      this.$emit('delete', this.dates)
    },

    setSignatureId(signatureId) {
      this.signature_id = signatureId
    },

    onSelectChange(selectedRowKeys) {
      this.dates = selectedRowKeys
    },

    formatDateString(date) {
      return moment.utc(date).tz(this.timeZone).format('MM/DD/YYYY')
    },

    getConflictedShift(id) {
      return this.timeOff.conflicts.find(item => item.id === id)?.shift
    },

    getCollectedDate(record) {
      const workStart = record.work_start ?? record.scheduled_start
      const workEnd = record.work_end ?? record.scheduled_end

      const workTime = this.formatTimeRange(workStart, workEnd)

      const date = util.convertDateTimeToCurrentTimeZoneWithFormat(
        workStart,
        this.$store.state.applicationState.dateFormat
      )

      const baseRows = [
        { label: 'Date', value: date },
        { label: 'Start', value: workTime?.start },
        { label: 'End', value: workTime?.end }
      ]

      return baseRows
    },

    isDisableDate(id) {
      return this.timeOff.conflicts
        .filter(item => item.shift !== null && ['started', 'completed'].includes(item.shift.status))
        .map(item => item.id)
        .includes(id)
    },

    async handleApprove(status) {
      if (status) {
        this.$emit('update:loadingApprove', true)
      } else {
        this.$emit('update:loadingDecline', true)
      }
      try {
        await this.$refs.signatureRef.saveSignature()

        if (!this.signature_id) return

        await this.approveDaysTimeOff({
          items: this.dates,
          id: this.timeOff.id,
          signature_id: this.signature_id,
          type: status ? 'approve' : 'decline'
        })
        this.$emit('approve')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        if (status) {
          this.$emit('update:loadingApprove', false)
        } else {
          this.$emit('update:loadingDecline', false)
        }
      }
    }
  },
  mounted() {
    this.scheduleSignatureKey += 1
    this.scheduleApprovedSignatureKey += 1
  }
}
</script>
